import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  AlertTriangle, Clock, Activity, Shield, Gauge, RefreshCcw,
  ChevronRight, Users, Zap, CheckCircle2, XCircle, AlertCircle, Eye
} from 'lucide-react';
import PageHeader from '../shared/PageHeader';
import StatusBadge from '../shared/StatusBadge';
import ScorePill from '../shared/ScorePill';
import { Skeleton } from '@/components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const OperatorDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/appsupport/operator-dashboard`);
      if (!res.ok) throw new Error('Failed to fetch dashboard');
      const d = await res.json();
      setData(d);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  if (loading) return (
    <div className="min-h-screen bg-background" data-testid="operator-dashboard-loading">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <Skeleton className="h-10 w-64 mb-6" />
        <div className="grid grid-cols-12 gap-4">
          <Skeleton className="col-span-12 xl:col-span-7 h-80" />
          <Skeleton className="col-span-12 xl:col-span-5 h-80" />
          <Skeleton className="col-span-12 lg:col-span-4 h-52" />
          <Skeleton className="col-span-12 lg:col-span-4 h-52" />
          <Skeleton className="col-span-12 lg:col-span-4 h-52" />
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Card className="p-8 text-center">
        <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
        <p className="text-foreground font-medium">Failed to load dashboard</p>
        <p className="text-muted-foreground text-sm mt-1">{error}</p>
        <Button onClick={fetchDashboard} className="mt-4" variant="outline">Retry</Button>
      </Card>
    </div>
  );

  const { active_incidents, hitl_queue, work_queue_health, signal_pipeline_health, application_estate_health } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="operator-dashboard">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <PageHeader
          title="Operator Dashboard"
          subtitle="Real-time operational overview - AppSupport AI Agent"
          onRefresh={fetchDashboard}
        />

        <div className="grid grid-cols-12 gap-4">
          {/* Panel 1: Active Incidents */}
          <Card className="col-span-12 xl:col-span-7" data-testid="operator-active-incidents-panel">
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[hsl(var(--status-crit))]" />
                  Active Incidents
                  <Badge variant="secondary" className="ml-1 text-xs">{active_incidents.length}</Badge>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[320px]">
                <div className="divide-y divide-border">
                  {active_incidents.length === 0 ? (
                    <div className="p-8 text-center" data-testid="panel-empty-state">
                      <CheckCircle2 className="h-8 w-8 text-[hsl(var(--status-ok))] mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No active incidents</p>
                    </div>
                  ) : (
                    active_incidents.map((inc) => (
                      <div key={inc.incident_id} className="px-4 py-3 hover:bg-muted/50 transition-colors duration-150" data-testid="active-incident-row">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs text-muted-foreground">{inc.incident_id}</span>
                              <ScorePill score={inc.business_impact_score} type="bis" />
                              <StatusBadge status={inc.status} />
                            </div>
                            <p className="text-sm font-medium text-foreground truncate">
                              {inc.workflow_name} - {inc.failure_step}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {inc.affected_record_count} records
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {inc.sla_exposure_minutes}m SLA
                              </span>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" className="shrink-0" onClick={() => navigate(`/appsupport/incidents/${inc.incident_id}`)} data-testid="active-incidents-open-button">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Panel 2: HITL Queue */}
          <Card className="col-span-12 xl:col-span-5" data-testid="operator-hitl-queue-panel">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-[hsl(var(--status-info))]" />
                HITL Queue
                <Badge variant="secondary" className="ml-1 text-xs">{hitl_queue.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[320px]">
                <div className="divide-y divide-border" data-testid="operator-hitl-queue-list">
                  {hitl_queue.length === 0 ? (
                    <div className="p-8 text-center" data-testid="panel-empty-state">
                      <CheckCircle2 className="h-8 w-8 text-[hsl(var(--status-ok))] mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No pending decisions</p>
                    </div>
                  ) : (
                    hitl_queue.map((item) => (
                      <div key={item.item_id} className="px-4 py-3 hover:bg-muted/50 transition-colors duration-150" data-testid="hitl-queue-item">
                        <div className="flex items-center gap-2 mb-1">
                          <StatusBadge status={item.reason} />
                          <span className="text-xs text-muted-foreground">{item.sla_exposure_minutes}m SLA</span>
                        </div>
                        <p className="text-sm font-medium text-foreground">{item.incident_summary}</p>
                        <p className="text-xs text-muted-foreground mt-1">{item.required_action}</p>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="default" className="h-7 text-xs" data-testid="hitl-queue-approve-button">Approve</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="hitl-queue-reject-button">Reject</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs">Needs Info</Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Panel 3: Work Queue Health */}
          <Card className="col-span-12 lg:col-span-4" data-testid="operator-work-queue-health-panel">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Zap className="h-4 w-4 text-[hsl(var(--status-warn))]" />
                Work Queue Health
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <div className="text-2xl font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{work_queue_health.total_items}</div>
                  <div className="text-xs text-muted-foreground">Total Items</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <div className="text-2xl font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{work_queue_health.pending}</div>
                  <div className="text-xs text-muted-foreground">Pending</div>
                </div>
              </div>
              <Separator />
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">At Risk</span>
                <span className="font-medium text-[hsl(var(--status-warn))]">{work_queue_health.at_risk}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Breached</span>
                <span className="font-medium text-[hsl(var(--status-crit))]">{work_queue_health.breached}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">7d Breach Rate</span>
                <span className="font-medium">{work_queue_health.sla_breach_rate_7d}%</span>
              </div>
            </CardContent>
          </Card>

          {/* Panel 4: Signal Pipeline Health */}
          <Card className="col-span-12 lg:col-span-4" data-testid="operator-signal-pipeline-health-panel">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Activity className="h-4 w-4 text-[hsl(var(--signal-good))]" />
                Signal Pipeline
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <ScrollArea className="h-[160px]">
                <div className="space-y-1.5">
                  {signal_pipeline_health.map((signal) => (
                    <div key={signal.signal_type} className="flex items-center justify-between text-xs py-1" data-testid="signal-pipeline-health-row">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${signal.status === 'active' ? 'bg-[hsl(var(--signal-good))]' : 'bg-[hsl(var(--status-unknown))]'}`}></span>
                        <span className="text-muted-foreground font-mono">{signal.signal_type.replace(/_/g, ' ')}</span>
                      </div>
                      <span className={`font-medium ${signal.status === 'active' ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {signal.status === 'active' ? `${signal.ingestion_count_1h}/h` : 'off'}
                      </span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Panel 5: Application Estate Health */}
          <Card className="col-span-12 lg:col-span-4" data-testid="operator-application-estate-health-panel">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Shield className="h-4 w-4 text-[hsl(var(--status-ok))]" />
                Estate Health
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-1 text-center">
                  <div className="text-xl font-semibold text-[hsl(var(--status-ok))]" style={{ fontFamily: 'var(--font-display)' }}>{application_estate_health.health_distribution?.HEALTHY || 0}</div>
                  <div className="text-xs text-muted-foreground">Healthy</div>
                </div>
                <div className="flex-1 text-center">
                  <div className="text-xl font-semibold text-[hsl(var(--status-warn))]" style={{ fontFamily: 'var(--font-display)' }}>{application_estate_health.health_distribution?.DEGRADED || 0}</div>
                  <div className="text-xs text-muted-foreground">Degraded</div>
                </div>
                <div className="flex-1 text-center">
                  <div className="text-xl font-semibold text-[hsl(var(--status-crit))]" style={{ fontFamily: 'var(--font-display)' }}>{application_estate_health.health_distribution?.CRITICAL || 0}</div>
                  <div className="text-xs text-muted-foreground">Critical</div>
                </div>
              </div>
              <Separator />
              <div className="text-xs text-muted-foreground">
                {application_estate_health.total_apps} total apps | {application_estate_health.active_monitoring} monitored
              </div>
              {application_estate_health.top_risky_apps?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Top Risk</p>
                  {application_estate_health.top_risky_apps.map((app) => (
                    <div key={app.application_id} className="flex items-center justify-between text-xs">
                      <span className="text-foreground truncate">{app.name}</span>
                      <ScorePill score={app.health_score} />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OperatorDashboard;
